import { ConfirmationDialog, FlatIconButton, Icon, Id, RadioControl, TextButton, Trigger } from "@dgs/core";
import { FieldArray, useField } from "formik";
import { TFunction } from "i18next";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Address } from "../../ui/Address";

import {
	ContactAttributeRequest,
	IContactAttributeBaseHandler,
	IContactAttributeHandler,
	IContactAttributeListHandler,
} from "../IContactAttributeHandler";
import { AddressFormFields, BaseAddressValue, validateAddress } from "./AddressFormFields";
import { RelationListRow, RelationListSection, TwoColumn } from "./RelationListSection";
import { usePrimaryRelationFieldArray } from "./usePrimaryRelationFieldArray";

interface AddressValue extends BaseAddressValue {
	id: Id | null;
	isPrimary: boolean;
}

interface AddressSaveRequest extends Omit<AddressValue, "country"> {
	countryId: Id;
}

const emptyValue: AddressValue = {
	id: null,
	additionalInfo: null,
	street: "",
	houseNumber: "",
	zip: "",
	city: "",
	country: null,
	type: null,
	isPrimary: false,
};

const isEmpty = (value: AddressValue) =>
	value.street === "" &&
	value.houseNumber === "" &&
	value.zip === "" &&
	value.city === "" &&
	value.country === null &&
	value.type === null;

export function createAddressesContactAttributeBaseHandler(id: Id): IContactAttributeBaseHandler {
	return {
		type: "ADDRESSES",
		id,
		getLabel: (t) => t("Addresses"),
	};
}

export function createAddressesContactAttributeProfileHandler(
	id: Id,
	value: AddressValue[],
): IContactAttributeHandler<AddressSaveRequest[], AddressValue[]> {
	const baseHandler = createAddressesContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		value: value.length === 0 ? [emptyValue] : value,
		validate(t, values) {
			const errors: Record<number, any> = {};
			let hasPrimary = false;
			let hasNonEmptyValues = false;

			values.forEach((address, index) => {
				if (isEmpty(address)) return;
				hasPrimary ||= address.isPrimary;
				hasNonEmptyValues = true;
				const addressErrors = validateAddress(t, address);
				if (Object.keys(addressErrors).length > 0) {
					errors[index] = addressErrors;
				}
			});

			if (!hasPrimary && hasNonEmptyValues) {
				return t("At least one address must be marked as primary");
			}

			return errors;
		},
		renderValue(): ReactNode {
			return (
				<ul>
					{value.map((address, index) => (
						<li key={index}>
							<Address address={address} />
						</li>
					))}
				</ul>
			);
		},
		renderFormField(_: TFunction, prefix): ReactNode {
			const name = `${prefix ? `${prefix}.` : ""}${id}.value`;
			return <FormField name={name} />;
		},
		toRequestValue(): ContactAttributeRequest<AddressSaveRequest[]> {
			return {
				id,
				value: this.value
					.filter((address) => !isEmpty(address))
					.map((address): AddressSaveRequest => ({ ...address, countryId: address.country?.id as string })),
			};
		},
	};
}

export function createAddressesContactAttributeListHandler(id: Id): IContactAttributeListHandler {
	const baseHandler = createAddressesContactAttributeBaseHandler(id);
	return {
		...baseHandler,
		getColumnSize: () => "1fr",
	};
}

const FormField: FC<{ name: string }> = ({ name }) => {
	const { t } = useTranslation();
	const { items, replace, push, remove } = usePrimaryRelationFieldArray<AddressValue>(name, emptyValue);
	const [, { error }] = useField(name);
	const hasOnlyOneEmptyItem = items.length === 1 && isEmpty(items[0]);

	return (
		<FieldArray name={name}>
			{() => (
				<RelationListSection title={t("Addresses")} error={typeof error === "string" ? error : undefined}>
					{items.map((address, index) => (
						<RelationListRow key={index}>
							<TwoColumn>
								<AddressFormFields name={`${name}.${index}`} />

								<RadioControl
									id={`address-${index}-primary`}
									name="addresses.isPrimary"
									value={String(index)}
									checked={address.isPrimary}
									label={t("Mark as primary")}
									onChange={(e) => replace(index, { ...address, isPrimary: e.target.value === String(index) })}
								/>
							</TwoColumn>
							<Trigger
								render={(dialogProps) => (
									<ConfirmationDialog
										heading={t("Delete address")}
										labels={{ confirm: t("Delete"), close: t("Cancel") }}
										open={dialogProps.open}
										onClose={dialogProps.handleClose}
										action={(close) => {
											remove(index);
											close();
										}}
									>
										{t("Are you sure you want to delete this address?")}
									</ConfirmationDialog>
								)}
							>
								{(triggerProps) => (
									<FlatIconButton
										{...triggerProps}
										type="button"
										title={t("Delete")}
										icon="delete"
										disabled={hasOnlyOneEmptyItem}
									/>
								)}
							</Trigger>
						</RelationListRow>
					))}
					<span>
						<TextButton onClick={() => push(emptyValue)} type="button" prefix={<Icon $icon="plus" />}>
							{t("Add address")}
						</TextButton>
					</span>
				</RelationListSection>
			)}
		</FieldArray>
	);
};
